import React from 'react';
import styled from 'styled-components';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Trash2,
  Wrench,
  XCircle,
} from 'lucide-react';
import { colors, spacing, typography, borderRadius } from '../../../styles/theme';
import type { ApprovalSummary } from '../../../services/adminAgentApi';

/**
 * Activity, approval and metadata cards for the admin agent transcript.
 *
 * These render sanitized summaries only. Raw tool arguments, query results and
 * model reasoning are deliberately not available to this layer.
 */

const Card = styled.div<{ $tone?: 'default' | 'danger' | 'success' }>`
  border: 1px solid ${props =>
    props.$tone === 'danger' ? colors.error[300] :
      props.$tone === 'success' ? colors.success[300] : colors.border.light};
  background: ${props =>
    props.$tone === 'danger' ? colors.error[50] :
      props.$tone === 'success' ? colors.success[50] : colors.background.secondary};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[3]};
  margin: ${spacing[2]} 0;
  font-size: ${typography.fontSizes.sm};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
`;

const Muted = styled.div`
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
  margin-top: ${spacing[1]};
  word-break: break-word;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  margin-top: ${spacing[2]};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-top: ${spacing[3]};
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' | 'ghost' }>`
  border-radius: ${borderRadius.base};
  border: 1px solid ${props =>
    props.$variant === 'danger' ? colors.error[600] :
      props.$variant === 'primary' ? colors.primary[600] : colors.border.normal};
  background: ${props =>
    props.$variant === 'danger' ? colors.error[600] :
      props.$variant === 'primary' ? colors.primary[600] : colors.white};
  color: ${props => (props.$variant === 'ghost' ? colors.text.primary : colors.white)};
  padding: ${spacing[2]} ${spacing[3]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Spinner = styled(Loader2)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export interface ToolActivity {
  toolCallId: string | null;
  toolName: string | null;
  status: 'running' | 'completed' | 'failed';
  inputSummary?: string | null;
  resultSummary?: string | null;
  durationMs?: number | null;
}

export function ToolCard({ activity, traceId }: { activity: ToolActivity; traceId?: string | null }) {
  const icon = activity.status === 'running'
    ? <Spinner size={16} aria-hidden />
    : activity.status === 'failed'
      ? <XCircle size={16} color={colors.error[600]} aria-hidden />
      : <CheckCircle2 size={16} color={colors.success[600]} aria-hidden />;

  return (
    <Card
      $tone={activity.status === 'failed' ? 'danger' : 'default'}
      role="group"
      aria-label={`Tool ${activity.toolName ?? 'unknown'} ${activity.status}`}
    >
      <CardHeader>
        {icon}
        <Wrench size={14} aria-hidden />
        {activity.toolName ?? 'tool'}
        <span aria-live="polite">{activity.status}</span>
      </CardHeader>
      {activity.inputSummary && <Muted>Input: {activity.inputSummary}</Muted>}
      {activity.resultSummary && <Muted>Result: {activity.resultSummary}</Muted>}
      <Muted>
        {activity.durationMs != null && `${activity.durationMs} ms · `}
        {traceId && `trace ${traceId.slice(0, 16)}`}
      </Muted>
    </Card>
  );
}

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[1]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
`;

/** Live "what is the agent doing right now" line, shown only while a turn runs. */
export function ActivityIndicator({ label }: { label: string }) {
  return (
    <StatusLine role="status" aria-live="polite">
      <Spinner size={16} aria-hidden />
      {label}
    </StatusLine>
  );
}

const OPERATION_LABELS: Record<string, string> = {
  add_entities: 'Create entities',
  add_event_sources: 'Create event sources',
  add_events: 'Create events',
  deactivate_records: 'Deactivate',
  hard_delete_records: 'Permanently delete',
  scale_resource: 'Scale Azure resource',
};

export function ApprovalCard({
  approval,
  busy,
  excludedRowIds,
  onToggleRow,
  onRespond,
}: {
  approval: ApprovalSummary;
  busy?: boolean;
  excludedRowIds: string[];
  onToggleRow: (rowId: string) => void;
  onRespond: (decision: 'approved' | 'rejected') => void;
}) {
  const isDelete = approval.irreversible;
  const label = OPERATION_LABELS[approval.operationType] ?? approval.operationType;
  const includedCount = approval.targets.length - excludedRowIds.length;

  return (
    <Card $tone={isDelete ? 'danger' : 'default'} role="group" aria-label={`Approval: ${label}`}>
      <CardHeader>
        {isDelete ? <Trash2 size={16} aria-hidden /> : <AlertTriangle size={16} aria-hidden />}
        {label}
        {approval.lifecycleAction && ` (${approval.lifecycleAction.replace('_', ' ')})`}
      </CardHeader>

      {isDelete && (
        <Muted>
          <strong>This permanently deletes data and cannot be undone.</strong>
        </Muted>
      )}

      <Row>
        <Muted>{approval.targets.length} target(s)</Muted>
        {approval.affectedCounts && (
          <Muted>
            Affects: {Object.entries(approval.affectedCounts)
              .map(([table, count]) => `${table} ${count}`)
              .join(', ')}
          </Muted>
        )}
        {approval.cascadeImpact && (
          <Muted>
            Cascade: {Object.entries(approval.cascadeImpact)
              .map(([table, count]) => `${table} ${count}`)
              .join(', ')}
          </Muted>
        )}
      </Row>

      {approval.targets.length > 1 && (
        <div>
          <Muted>Deselect any row to exclude it from this batch:</Muted>
          {approval.targets.map((target: Record<string, unknown>, index: number) => {
            const rowId = String((target as { rowId?: string }).rowId ?? index);
            const excluded = excludedRowIds.includes(rowId);
            return (
              <label
                key={rowId}
                style={{ display: 'block', marginTop: spacing[1], fontSize: typography.fontSizes.xs }}
              >
                <input
                  type="checkbox"
                  checked={!excluded}
                  onChange={() => onToggleRow(rowId)}
                  aria-label={`Include row ${rowId}`}
                />{' '}
                {JSON.stringify(target).slice(0, 120)}
              </label>
            );
          })}
        </div>
      )}

      {approval.warnings?.length > 0 && (
        <Muted>Warnings: {approval.warnings.join('; ')}</Muted>
      )}

      <Muted>
        Expires {new Date(approval.expiresAt).toLocaleString()} · hash{' '}
        {approval.proposalHash.slice(0, 12)}
        {approval.traceId && ` · trace ${approval.traceId.slice(0, 16)}`}
      </Muted>

      <Actions>
        <Button
          $variant={isDelete ? 'danger' : 'primary'}
          disabled={busy || includedCount === 0}
          onClick={() => onRespond('approved')}
        >
          {isDelete ? `Permanently delete ${includedCount} record(s)` : `Approve ${includedCount} row(s)`}
        </Button>
        <Button $variant="ghost" disabled={busy} onClick={() => onRespond('rejected')}>
          Reject
        </Button>
      </Actions>
    </Card>
  );
}

export function UsageCard({
  usage,
}: {
  usage: {
    inputTokens?: number | null;
    outputTokens?: number | null;
    reasoningTokens?: number | null;
    cachedInputTokens?: number | null;
    estimatedCost?: { totalCost?: number; currency?: string; priceListVersion?: string } | null;
    model?: string | null;
    reasoningEffort?: string | null;
    elapsedMs?: number | null;
    traceId?: string | null;
  };
}) {
  const parts = [
    usage.model && `model ${usage.model}`,
    usage.reasoningEffort && `effort ${usage.reasoningEffort}`,
    usage.inputTokens != null && `in ${usage.inputTokens}`,
    usage.cachedInputTokens != null && `cached ${usage.cachedInputTokens}`,
    usage.outputTokens != null && `out ${usage.outputTokens}`,
    usage.reasoningTokens != null && `reasoning ${usage.reasoningTokens}`,
    usage.elapsedMs != null && `${usage.elapsedMs} ms`,
  ].filter(Boolean) as string[];

  return (
    <Muted>
      {parts.join(' · ')}
      {usage.estimatedCost?.totalCost != null && (
        <>
          {' · '}
          <span title={`Price list ${usage.estimatedCost.priceListVersion ?? 'unknown'}`}>
            est. {usage.estimatedCost.currency ?? 'USD'} {usage.estimatedCost.totalCost.toFixed(4)}
            {' (estimate)'}
          </span>
        </>
      )}
      {usage.traceId && ` · trace ${usage.traceId.slice(0, 16)}`}
    </Muted>
  );
}

export { Card as AgentCard, Button as AgentButton, Muted as AgentMuted };
